import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  cancelShift as cancelShiftApi,
  createPosting as createPostingApi,
  getShift,
  listMyVenueShifts,
  listOpenShifts,
  updateShift as updateShiftApi,
} from "@/features/shifts/api/shiftsApi";
import type {
  CreatePostingPayload,
  ShiftFilters,
  ShiftStatus,
  UpdateShiftPayload,
} from "@/features/shifts/types";

export function useOpenShifts(filters: ShiftFilters) {
  return useQuery({
    queryKey: ["shifts", "open", filters],
    queryFn: () => listOpenShifts(filters),
  });
}

export function useMyVenueShifts(status?: ShiftStatus) {
  return useQuery({
    queryKey: ["shifts", "mine", status ?? "all"],
    queryFn: () => listMyVenueShifts(status),
  });
}

export function useShift(id: string | undefined) {
  return useQuery({
    queryKey: ["shifts", "detail", id],
    queryFn: () => getShift(id!),
    enabled: !!id,
  });
}

export function useCreatePosting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePostingPayload) => createPostingApi(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shifts"] });
      // New shifts must appear on the ops dashboard immediately.
      qc.invalidateQueries({ queryKey: ["ops", "upcoming-shifts"] });
    },
  });
}

export function useUpdateShift(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateShiftPayload) => updateShiftApi(id, payload),
    onSuccess: (data) => {
      qc.setQueryData(["shifts", "detail", id], data);
      qc.invalidateQueries({ queryKey: ["shifts"] });
      // Pay rate / role / time may have changed — applicants and the
      // worker's "my applications" view need to reflect the new details.
      qc.invalidateQueries({ queryKey: ["applicants", id] });
      qc.invalidateQueries({ queryKey: ["applications"] });
      // Ops dashboard rows embed role / time / pay_rate / urgency too.
      qc.invalidateQueries({ queryKey: ["ops", "upcoming-shifts"] });
    },
  });
}

export function useCancelShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cancelShiftApi(id),
    onSuccess: (data) => {
      qc.setQueryData(["shifts", "detail", data.id], data);
      qc.invalidateQueries({ queryKey: ["shifts"] });
      // Cancelling rejects every applicant and ends any in-progress events.
      qc.invalidateQueries({ queryKey: ["applicants", data.id] });
      qc.invalidateQueries({ queryKey: ["applications"] });
      qc.invalidateQueries({ queryKey: ["shift-events", data.id] });
      // Cancelled shifts drop out of the ops upcoming queue.
      qc.invalidateQueries({ queryKey: ["ops", "upcoming-shifts"] });
    },
  });
}
